import {
  type CylinderParams,
  checkerPattern,
  type Raster,
  type Rect,
  warpCylinder,
} from '@repo/warp-engine';

/**
 * 미리보기 원본 픽셀 수 상한. 출력은 이것의 약 2/π 배이고, 워프가 출력 픽셀당 약 20–25ns 라 허용 시간 100ms(2026-10-06 확정) 안에 든다.
 */
export const MAX_PREVIEW_SOURCE_PIXELS = 2_000_000;

/** 체크무늬 가로 칸 수. 칸 크기를 주입하지 않으면 원본 폭을 이 수로 나눈다. */
const CHECKER_COLUMNS = 24;

/** 미리보기 래스터와 그 래스터를 그릴 사진 px 사각형. */
export interface CylinderPreviewRaster {
  readonly raster: Raster;
  readonly placement: Rect;
}

export interface CylinderPreviewOptions {
  /** 체크무늬 칸 크기(원본 px, 양의 정수). 스펙이 마플 원값 체크무늬와 비교할 때 준다. */
  readonly checkerCell?: number;
}

/**
 * 체크무늬를 원통에 감은 미리보기를 만든다. 사진 px 기준 area 가 상한보다 크면 원본을 줄여서 워프하고,
 * 배치는 다시 사진 px 로 되돌린다. 원본 폭·높이는 정수로 다시 잡으므로 어댑터가 낸 area 의 부동소수 꼬리는 사라진다.
 * params 가 validateCylinder 를 통과하지 못하면 RangeError 다.
 */
export const renderCylinderPreview = (
  params: CylinderParams,
  { checkerCell }: CylinderPreviewOptions = {},
): CylinderPreviewRaster => {
  const { area } = params;
  const shrink = Math.min(1, Math.sqrt(MAX_PREVIEW_SOURCE_PIXELS / (area.width * area.height)));
  const width = Math.max(1, Math.round(area.width * shrink));
  const height = Math.max(1, Math.round(area.height * shrink));
  const scaleX = width / area.width;
  const scaleY = height / area.height;
  const scaled: CylinderParams = {
    area: { left: area.left * scaleX, top: area.top * scaleY, width, height },
    upSide: params.upSide * scaleY,
    downSide: params.downSide * scaleY,
    degree: params.degree,
  };
  const cell = checkerCell ?? Math.max(1, Math.round(width / CHECKER_COLUMNS));
  const { raster, placement } = warpCylinder(checkerPattern(width, height, cell), scaled);
  return {
    raster,
    placement: {
      left: placement.left / scaleX,
      top: placement.top / scaleY,
      width: placement.width / scaleX,
      height: placement.height / scaleY,
    },
  };
};
